import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const SuccessMetricsSection = () => {
  const [animatedValues, setAnimatedValues] = useState({
    totalWinnings: 0,
    avgPayoutTime: 0,
    satisfaction: 0,
    activeWinners: 0
  });

  const finalValues = {
    totalWinnings: 50000000, // 5 Crores
    avgPayoutTime: 25, // 25 minutes
    satisfaction: 98.5, // 98.5%
    activeWinners: 12500 // 12.5K winners
  };

  useEffect(() => {
    const duration = 2000; // 2 seconds
    const steps = 60;
    const stepDuration = duration / steps;

    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      
      setAnimatedValues({
        totalWinnings: Math.floor(finalValues?.totalWinnings * progress),
        avgPayoutTime: Math.floor(finalValues?.avgPayoutTime * progress),
        satisfaction: Math.floor(finalValues?.satisfaction * progress * 10) / 10,
        activeWinners: Math.floor(finalValues?.activeWinners * progress)
      });

      if (currentStep >= steps) {
        clearInterval(timer);
        setAnimatedValues(finalValues);
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, []);

  const metrics = [
    {
      id: 1,
      icon: "TrendingUp",
      title: "Total Winnings Distributed",
      value: `₹${(animatedValues?.totalWinnings / 10000000)?.toFixed(1)}Cr`,
      subtitle: "Paid to our winners",
      color: "text-success",
      bgColor: "bg-success/10"
    },
    {
      id: 2,
      icon: "Clock",
      title: "Average Payout Time",
      value: `${animatedValues?.avgPayoutTime}min`,
      subtitle: "Lightning fast withdrawals",
      color: "text-primary",
      bgColor: "bg-primary/10"
    },
    {
      id: 3,
      icon: "Heart",
      title: "Customer Satisfaction",
      value: `${animatedValues?.satisfaction}%`,
      subtitle: "Happy players rating",
      color: "text-accent",
      bgColor: "bg-accent/10"
    },
    {
      id: 4,
      icon: "Users",
      title: "Active Winners",
      value: `${(animatedValues?.activeWinners / 1000)?.toFixed(1)}K+`,
      subtitle: "This month alone",
      color: "text-warning",
      bgColor: "bg-warning/10"
    }
  ];

  const achievements = [
    {
      icon: "Shield",
      title: "100% Secure Platform",
      description: "Bank-grade security for all transactions"
    },
    {
      icon: "Zap",
      title: "Instant Payouts",
      description: "Fastest withdrawal processing in India"
    },
    {
      icon: "Award",
      title: "Trusted by Thousands",
      description: "India\'s most reliable gaming platform"
    },
    {
      icon: "MessageCircle",
      title: "24/7 WhatsApp Support",
      description: "Personal gaming expert always available"
    }
  ];

  return (
    <section className="py-16 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-gradient-royal mb-4">
            Success by Numbers
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Real metrics from real winners who chose Nitin Online Hub for their gaming journey
          </p>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {metrics?.map((metric) => (
            <div
              key={metric?.id}
              className="bg-card rounded-card shadow-subtle hover:shadow-royal transition-royal p-6 text-center group"
            >
              <div className={`w-16 h-16 ${metric?.bgColor} rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-royal`}>
                <Icon name={metric?.icon} size={28} className={metric?.color} />
              </div>
              
              <div className="text-3xl font-heading font-bold text-text-primary mb-2">
                {metric?.value}
              </div>
              
              <h3 className="font-semibold text-text-primary mb-1">
                {metric?.title}
              </h3>
              
              <p className="text-sm text-text-secondary">
                {metric?.subtitle}
              </p>
            </div>
          ))}
        </div>

        {/* Achievements Section */}
        <div className="bg-surface-secondary rounded-card p-8">
          <h3 className="text-2xl font-heading font-bold text-center text-text-primary mb-8">
            Why Winners Choose Nitin
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {achievements?.map((achievement, index) => (
              <div
                key={index}
                className="text-center group"
              >
                <div className="w-12 h-12 bg-gradient-royal rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-royal">
                  <Icon name={achievement?.icon} size={20} color="white" />
                </div>
                
                <h4 className="font-semibold text-text-primary mb-2">
                  {achievement?.title}
                </h4>
                
                <p className="text-sm text-text-secondary">
                  {achievement?.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-12 text-center">
          <div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
            <div className="flex items-center space-x-2">
              <Icon name="Shield" size={20} color="var(--color-success)" />
              <span className="text-sm font-medium">SSL Secured</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="CheckCircle" size={20} color="var(--color-success)" />
              <span className="text-sm font-medium">RNG Certified</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Lock" size={20} color="var(--color-success)" />
              <span className="text-sm font-medium">Data Protected</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Verified" size={20} color="var(--color-success)" />
              <span className="text-sm font-medium">Licensed Platform</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SuccessMetricsSection;